import {FC, useState, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {CheckCircle2, Lock, ChevronDown, ChevronUp, GraduationCap} from "lucide-react";
import accountantApi from "api/accountant";
import shareApi from "api/share";
import useMaybeNotSetupUser from "contexts/MaybeNotSetupUser/useMaybeNotSetupUser";

type Pathways = Awaited<ReturnType<typeof accountantApi.getCasPathways>>;
type Pathway = Pathways[number];
type PathwayCourse = Pathway["courses"][number];
type PathwayModule = PathwayCourse["modules"][number];
type BwScores = Awaited<ReturnType<typeof accountantApi.getBookWidgetsScores>>;
type BwWidget = BwScores[string]["widgets"][number];
type CourseGroups = Awaited<ReturnType<typeof accountantApi.getCourseGroups>>;

type ItemStatus = "complete" | "active" | "available" | "locked";
type FilterTab = "all" | "completed" | "in_progress" | "locked";

const BOTTOM_SECTION_COURSE_IDS = new Set([1140558, 1261833]);

// ─── Timeline item types ────────────────────────────────────────────────────

interface TimelineRow {
  key: string;
  label: string;
  status: ItemStatus;
  score: number | null;
  avgScore: number | null;
  addOnGroup: string | null;
  groupLabel: string | null;
  widgets: BwWidget[];
}

interface MergedMilestone {
  tagName: string;
  certLabel: string | null;
  certImageUrl: string | null;
  isEarned: boolean;
}

type TimelineItem = {type: "row"; data: TimelineRow} | {type: "milestone"; data: MergedMilestone};

interface Summary {
  completedCourses: number;
  inProgressCourses: number;
  lockedCourses: number;
  avgScore: number | null;
  akadianAvgScore: number | null;
  atNinetyPlus: number | null;
}

// ─── Status derivation — exact replica of akadian deriveModuleItems ─────────

function isModuleComplete(mod: PathwayModule): boolean {
  if (mod.isComplete) return true;
  if (mod.lessonCount > 0 && mod.completedLessonCount >= mod.lessonCount) return true;
  return false;
}

function sortPathwayCourses(pathway: Pathway, hiddenGroups: string[]): Pathway {
  const isHidden = (c: PathwayCourse) =>
    BOTTOM_SECTION_COURSE_IDS.has(c.courseId) || (c.addOnGroup != null && hiddenGroups.includes(c.addOnGroup));
  return {
    ...pathway,
    courses: [...pathway.courses].sort((a, b) => {
      const aH = isHidden(a) ? 1 : 0;
      const bH = isHidden(b) ? 1 : 0;
      if (aH !== bH) return aH - bH;
      return a.courseOrder - b.courseOrder;
    }),
  };
}

/** Flattens a pathway into timeline rows — mirrors akadian deriveModuleItems + PathwayBlock */
function derivePathwayRows(pathway: Pathway, scores: BwScores, hiddenGroups: string[]): TimelineItem[] {
  const sorted = sortPathwayCourses(pathway, hiddenGroups);
  const items: TimelineItem[] = [];
  let foundActive = false;

  // Milestone lookup
  const milestoneAfterMap = new Map<number | null, Pathway["milestones"]>();
  for (const m of sorted.milestones) {
    const key = m.afterPathwayCourseId;
    if (!milestoneAfterMap.has(key)) milestoneAfterMap.set(key, []);
    milestoneAfterMap.get(key)!.push(m);
  }

  for (const course of sorted.courses) {
    // Skip bottom-section and hidden add-on courses
    if (BOTTOM_SECTION_COURSE_IDS.has(course.courseId)) continue;
    if (course.addOnGroup != null && hiddenGroups.includes(course.addOnGroup)) continue;

    const isModuleDisplay = course.courseGroupDisplayMode === "modules";

    if (isModuleDisplay) {
      // Module-level: one row per module (CourseSection in akadian)
      const mods = [...course.modules].sort((a, b) => a.modulePosition - b.modulePosition);
      for (const mod of mods) {
        let status: ItemStatus;
        if (!course.isPurchased) {
          status = "locked";
        } else if (isModuleComplete(mod)) {
          status = "complete";
        } else if (foundActive) {
          status = "available";
        } else {
          foundActive = true;
          status = "active";
        }

        const bw = scores[String(mod.moduleId)];
        items.push({
          type: "row",
          data: {
            key: `mod-${mod.moduleId}`,
            label: mod.moduleLabel,
            status,
            score: bw?.score != null ? Math.round(bw.score * 100) : null,
            avgScore: bw?.avgScore != null ? Math.round(bw.avgScore * 100) : null,
            addOnGroup: course.addOnGroup,
            groupLabel: course.groupLabel,
            widgets: bw?.widgets ?? [],
          },
        });
      }
    } else {
      // Course-level: one row per course (IntuitCourseRow in akadian)
      let status: ItemStatus;
      if (!course.isPurchased) {
        status = "locked";
      } else if (course.isComplete === true) {
        status = "complete";
      } else if (foundActive) {
        status = "available";
      } else {
        foundActive = true;
        status = "active";
      }

      const courseWidgets: BwWidget[] = [];
      let userScoreSum = 0,
        userScoreCount = 0,
        avgScoreSum = 0,
        avgScoreCount = 0;
      for (const mod of course.modules) {
        const bw = scores[String(mod.moduleId)];
        if (bw) {
          courseWidgets.push(...bw.widgets);
          if (bw.score != null) {
            userScoreSum += bw.score;
            userScoreCount++;
          }
          if (bw.avgScore != null) {
            avgScoreSum += bw.avgScore;
            avgScoreCount++;
          }
        }
      }

      items.push({
        type: "row",
        data: {
          key: `course-${course.courseId}`,
          label: course.courseTitle,
          status,
          score: userScoreCount > 0 ? Math.round((userScoreSum / userScoreCount) * 100) : null,
          avgScore: avgScoreCount > 0 ? Math.round((avgScoreSum / avgScoreCount) * 100) : null,
          addOnGroup: course.addOnGroup,
          groupLabel: course.groupLabel,
          widgets: courseWidgets,
        },
      });
    }

    // Milestones after this course
    const after = milestoneAfterMap.get(course.pathwayCourseId);
    if (after) {
      for (const m of after) {
        items.push({
          type: "milestone",
          data: {tagName: m.tagName, certLabel: m.certLabel, certImageUrl: m.certImageUrl, isEarned: m.isEarned},
        });
      }
    }
  }

  return items;
}

// ─── Build full timeline (all pathway types, same order as akadian page) ────

function buildTimeline(
  pathways: Pathways,
  scores: BwScores,
  addOnPreferences: Record<string, boolean>,
  courseGroups: CourseGroups,
): TimelineItem[] {
  const hiddenGroups = courseGroups
    .filter((g) => g.visibilityEditable && !(addOnPreferences[g.groupKey] ?? g.defaultEnabled))
    .map((g) => g.groupKey);

  const sorted = [...pathways].sort((a, b) => a.pathwayOrder - b.pathwayOrder);
  const items: TimelineItem[] = [];

  // CAS core pathways
  for (const p of sorted.filter((p) => p.pathwayType === "cas")) {
    items.push(...derivePathwayRows(p, scores, hiddenGroups));
  }

  // Intuit pathways
  const showIntuit =
    addOnPreferences["intuit"] ?? courseGroups.find((g) => g.groupKey === "intuit")?.defaultEnabled ?? true;
  if (showIntuit) {
    for (const p of sorted.filter((p) => p.pathwayType === "intuit")) {
      items.push(...derivePathwayRows(p, scores, []));
    }
  }

  // Specialty pathways
  const showSpecialty =
    addOnPreferences["specialty"] ?? courseGroups.find((g) => g.groupKey === "specialty")?.defaultEnabled ?? false;
  if (showSpecialty) {
    for (const p of sorted.filter((p) => p.pathwayType === "specialty")) {
      items.push(...derivePathwayRows(p, scores, []));
    }
  }

  return items;
}

function computeSummary(items: TimelineItem[]): Summary {
  const rows = items.filter((i): i is {type: "row"; data: TimelineRow} => i.type === "row").map((i) => i.data);
  const completedCourses = rows.filter((c) => c.status === "complete").length;
  const inProgressCourses = rows.filter((c) => c.status === "active").length;
  const lockedCourses = rows.filter((c) => c.status === "locked" || c.status === "available").length;

  // Compute from individual widget scores, not module averages
  const allWidgets = rows.flatMap((r) => r.widgets);
  const scoredWidgets = allWidgets.filter((w) => w.bestScore != null);
  const avgScore =
    scoredWidgets.length > 0
      ? Math.round((scoredWidgets.reduce((s, w) => s + w.bestScore!, 0) / scoredWidgets.length) * 100)
      : null;

  const scoredWidgetsAvg = allWidgets.filter((w) => w.bestScore != null);
  const atNinetyPlus =
    scoredWidgetsAvg.length > 0
      ? Math.round((scoredWidgetsAvg.filter((w) => w.bestScore! >= 0.9).length / scoredWidgetsAvg.length) * 100)
      : null;

  // Akadian avg from module-level avgScore (global, not user-specific)
  const withAvg = rows.filter((c) => c.avgScore != null);
  const akadianAvgScore =
    withAvg.length > 0 ? Math.round(withAvg.reduce((s, c) => s + c.avgScore!, 0) / withAvg.length) : null;

  return {completedCourses, inProgressCourses, lockedCourses, avgScore, akadianAvgScore, atNinetyPlus};
}

// ─── Score color ────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 90) return "var(--color-aka-progress)";
  if (score >= 70) return "var(--color-aka-amber)";
  return "var(--color-aka-notify)";
}

// ─── Status dot (same as akadian Progress page) ────────────────────────────

const StatusDot: FC<{status: ItemStatus; isIntuit?: boolean}> = ({status, isIntuit}) => {
  if (status === "complete") {
    return (
      <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 bg-aka-progress border-aka-progress">
        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
      </div>
    );
  }
  if (status === "active") {
    return (
      <div
        className="w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 bg-aka-amber border-aka-amber"
        style={{boxShadow: "0 0 0 3px var(--color-aka-amber-bg)"}}
      >
        <span className="block w-2 h-2 rounded-full bg-white animate-pulse" />
      </div>
    );
  }
  if (status === "available") {
    if (isIntuit) {
      return (
        <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 bg-aka-intuit-bg border-aka-intuit-border">
          <GraduationCap className="w-3 h-3 text-aka-intuit" />
        </div>
      );
    }
    return <div className="w-7 h-7 rounded-full border-2 flex-shrink-0 bg-white border-aka-neutral" />;
  }
  // locked
  return (
    <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 bg-aka-locked-bg border-aka-neutral">
      <Lock className="w-3 h-3 text-aka-neutral" />
    </div>
  );
};

// ─── Widget type badge ──────────────────────────────────────────────────────

const WidgetTypeBadge: FC<{widgetType: string | null}> = ({widgetType}) => {
  const label = widgetType || "Activity";
  const isTest = label.toLowerCase().includes("test") || label.toLowerCase().includes("exam");
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 text-center"
      style={{
        backgroundColor: isTest ? "oklch(0.92 0.04 280 / 0.4)" : "oklch(0.92 0.06 42 / 0.3)",
        color: isTest ? "var(--color-aka-intuit)" : "var(--color-aka-amber-mid)",
        minWidth: 28,
      }}
    >
      {label.charAt(0).toUpperCase() + label.slice(1)}
    </span>
  );
};

// ─── Summary stats ──────────────────────────────────────────────────────────

const SummaryStats: FC<{summary: Summary}> = ({summary}) => {
  const stats = [
    {value: summary.completedCourses, label: "Courses complete", color: "var(--color-aka-progress)"},
    {value: summary.inProgressCourses, label: "In progress", color: "var(--color-aka-amber)"},
    {value: summary.lockedCourses, label: "Not yet started", color: "var(--color-aka-subtle)"},
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-xl p-3 border bg-aka-surface border-aka-border"
          style={{boxShadow: "0 1px 6px oklch(0.2 0.04 40 / 0.04)"}}
        >
          <p className="text-xl font-bold" style={{color: s.color}}>
            {s.value}
          </p>
          <p className="text-xs mt-0.5 text-aka-subtle">{s.label}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Filter buttons ─────────────────────────────────────────────────────────

const FilterButtons: FC<{
  activeFilter: FilterTab;
  setActiveFilter: (f: FilterTab) => void;
  counts: Record<FilterTab, number>;
}> = ({activeFilter, setActiveFilter, counts}) => {
  const tabs: {key: FilterTab; label: string}[] = [
    {key: "all", label: "All Courses"},
    {key: "completed", label: "Completed"},
    {key: "in_progress", label: "In progress"},
    {key: "locked", label: "Locked"},
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map(({key, label}) => {
        const isActive = activeFilter === key;
        return (
          <button
            key={key}
            onClick={() => setActiveFilter(key)}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium transition-all"
            style={{
              color: isActive ? "var(--color-aka-heading)" : "var(--color-aka-subtle)",
              backgroundColor: isActive ? "oklch(0.92 0.04 275 / 0.15)" : "transparent",
              borderColor: isActive ? "oklch(0.72 0.08 275)" : "var(--color-aka-neutral)",
            }}
          >
            {label}
            <span
              className="text-[10px] font-semibold px-1 rounded-full"
              style={
                isActive
                  ? {color: "var(--color-aka-heading)"}
                  : {backgroundColor: "var(--color-aka-tag-bg)", color: "var(--color-aka-faint)"}
              }
            >
              {counts[key]}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ─── Overall summary bar ────────────────────────────────────────────────────

const OverallBar: FC<{summary: Summary; filterLabel: string}> = ({summary, filterLabel}) => (
  <div
    className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-4"
    style={{backgroundColor: "var(--color-aka-surface-faint)", border: "2px solid oklch(0.84 0.015 75)"}}
  >
    <span className="flex-1 min-w-32 text-sm font-bold text-aka-heading">Overall — {filterLabel}</span>

    {summary.avgScore != null && (
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end">
          <span className="text-[10px] uppercase tracking-wide text-aka-faint">Avg score</span>
          <span className="text-base font-bold text-aka-amber">{summary.avgScore}%</span>
        </div>
        <div className="w-px h-8 bg-aka-border" />
        <div className="flex flex-col items-start">
          <span className="text-[10px] uppercase tracking-wide text-aka-faint">Akadian avg</span>
          <span className="text-base font-bold text-aka-subtle">
            {summary.akadianAvgScore != null ? `${summary.akadianAvgScore}%` : "—"}
          </span>
        </div>
      </div>
    )}

    {summary.atNinetyPlus != null && summary.atNinetyPlus > 0 && (
      <div className="flex flex-col items-center">
        <span className="text-[10px] uppercase tracking-wide text-aka-faint">At 90%+</span>
        <span className="text-base font-bold text-aka-progress">{summary.atNinetyPlus}%</span>
      </div>
    )}

    {summary.avgScore != null && (
      <div className="flex flex-col gap-1 w-28">
        <div className="flex items-center gap-1">
          <span className="text-[9px] w-10 text-right text-aka-faint">You</span>
          <div className="flex-1 h-1.5 rounded-full bg-aka-surface-base">
            <div className="h-1.5 rounded-full bg-aka-amber" style={{width: `${summary.avgScore}%`}} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] w-10 text-right text-aka-faint">Akadian</span>
          <div className="flex-1 h-1.5 rounded-full bg-aka-surface-base">
            <div
              className="h-1.5 rounded-full"
              style={{width: `${summary.akadianAvgScore ?? 0}%`, backgroundColor: "oklch(0.72 0.01 265)"}}
            />
          </div>
        </div>
      </div>
    )}
  </div>
);

// ─── Add-on toggle row ──────────────────────────────────────────────────────

const AddOnToggles: FC<{
  groups: {key: string; label: string}[];
  enabledGroups: Record<string, boolean>;
  access: Record<string, boolean>;
  onToggle: (group: string) => void;
}> = ({groups, enabledGroups, access, onToggle}) => {
  if (groups.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-3 rounded-xl bg-aka-surface border border-aka-border">
      <span className="text-xs font-semibold uppercase tracking-wide text-aka-subtle">Add-ons:</span>
      {groups.map(({key, label}) => {
        const enabled = enabledGroups[key] ?? false;
        const available = access[key] ?? false;
        return (
          <button
            key={key}
            role="switch"
            aria-checked={enabled && available}
            onClick={() => available && onToggle(key)}
            className="flex items-center gap-2"
            style={{opacity: available ? 1 : 0.45, cursor: available ? "pointer" : "default"}}
            disabled={!available}
          >
            <span
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                width: 36,
                height: 20,
                borderRadius: 999,
                cursor: available ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
                backgroundColor: enabled && available ? "var(--color-aka-amber)" : "var(--color-aka-neutral-off)",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 2,
                  left: enabled && available ? 18 : 2,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                  transition: "left 0.2s",
                }}
              />
            </span>
            <span className={`text-xs font-medium ${enabled && available ? "text-aka-amber-mid" : "text-aka-subtle"}`}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ─── Timeline row ───────────────────────────────────────────────────────────

const LTI_LAUNCH_BASE = "https://helpdesk.akadian.com/lti/launch";

const RowItem: FC<{row: TimelineRow; email?: string}> = ({row, email}) => {
  const [open, setOpen] = useState(false);
  const isIntuit = row.addOnGroup != null;
  const isUnlocked = row.status === "complete" || row.status === "active" || row.status === "available";
  const hasWidgets = row.widgets.length > 0;

  const onWidgetClick = (w: BwWidget) => {
    if (!email || !isUnlocked || !w.playUrl) return;
    const url = `${LTI_LAUNCH_BASE}?email=${encodeURIComponent(email)}&widget_id=${encodeURIComponent(w.playUrl)}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      <div
        className="flex items-center gap-3 py-1.5 px-1 rounded-lg pr-3 transition-colors"
        style={{
          backgroundColor: open ? "var(--color-aka-page)" : "transparent",
          cursor: hasWidgets ? "pointer" : "default",
        }}
        onClick={hasWidgets ? () => setOpen((v) => !v) : undefined}
      >
        <StatusDot status={row.status} isIntuit={isIntuit} />

        <div className="flex-1 min-w-0">
          <span
            className="text-sm leading-tight block"
            style={{
              color:
                row.status === "locked" || row.status === "available"
                  ? "var(--color-aka-subtle)"
                  : row.status === "active"
                    ? "var(--color-aka-amber-mid)"
                    : "var(--color-aka-heading-soft)",
              fontWeight: row.status === "active" ? 700 : row.status === "complete" ? 600 : 400,
            }}
          >
            {row.label}
          </span>
          {isIntuit && row.groupLabel && (
            <span className="text-[9px] px-1 rounded bg-aka-intuit-bg text-aka-intuit">{row.groupLabel}</span>
          )}
        </div>

        {row.score != null ? (
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
            style={{color: scoreColor(row.score)}}
          >
            {row.score}%
          </span>
        ) : row.widgets.length === 0 && row.status !== "locked" ? (
          <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 text-aka-faint">n/a</span>
        ) : null}

        {row.score != null && row.avgScore != null && (
          <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 hidden sm:inline bg-aka-tag-bg text-aka-subtle border border-aka-border">
            akadian avg {row.avgScore}%
          </span>
        )}

        {hasWidgets && (
          <span className="text-aka-faint flex-shrink-0">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </span>
        )}
      </div>

      {open && row.widgets.length > 0 && (
        <div className="ml-10 mt-0.5 mb-1.5 overflow-hidden border border-aka-border rounded-lg">
          {row.widgets.map((w, i) => {
            const clickable = !!email && isUnlocked && !!w.playUrl;
            return (
              <div
                key={w.widgetId}
                className="flex items-center gap-3 px-3 py-2 transition-colors"
                style={{
                  backgroundColor: i % 2 === 0 ? "var(--color-aka-surface)" : "var(--color-aka-surface-faint)",
                  borderBottom: i < row.widgets.length - 1 ? "1px solid var(--color-aka-border-light)" : "none",
                  cursor: clickable ? "pointer" : "default",
                }}
                onClick={clickable ? () => onWidgetClick(w) : undefined}
              >
                <WidgetTypeBadge widgetType={w.widgetType} />
                <span className="flex-1 text-xs text-aka-heading-soft">{w.widgetName || "Untitled"}</span>
                {w.bestScore != null ? (
                  <span
                    className="text-xs font-bold flex-shrink-0"
                    style={{color: scoreColor(Math.round(w.bestScore * 100)), minWidth: 32, textAlign: "right"}}
                  >
                    {Math.round(w.bestScore * 100)}%
                  </span>
                ) : (
                  <span className="text-xs text-aka-faint flex-shrink-0" style={{minWidth: 32, textAlign: "right"}}>
                    —
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Milestone row ──────────────────────────────────────────────────────────

const MilestoneRow: FC<{milestone: MergedMilestone}> = ({milestone}) => (
  <div className="flex items-center gap-3 py-0.5 px-1">
    <div className="flex-shrink-0" style={{width: 28, display: "flex", justifyContent: "center"}}>
      {milestone.certImageUrl ? (
        <img
          alt={milestone.certLabel || milestone.tagName}
          width={24}
          height={24}
          src={milestone.certImageUrl}
          style={{filter: milestone.isEarned ? "none" : "grayscale(100%) opacity(0.45)"}}
        />
      ) : (
        <div
          className="w-6 h-6 rounded-full border-2 flex items-center justify-center"
          style={{
            backgroundColor: milestone.isEarned ? "var(--color-aka-progress)" : "var(--color-aka-locked-bg)",
            borderColor: milestone.isEarned ? "var(--color-aka-progress)" : "var(--color-aka-neutral)",
          }}
        >
          {milestone.isEarned && <CheckCircle2 className="w-3 h-3 text-white" />}
        </div>
      )}
    </div>
    <span
      className="text-xs font-medium"
      style={{color: milestone.isEarned ? "var(--color-aka-heading-soft)" : "oklch(0.62 0.02 70)"}}
    >
      {milestone.certLabel || milestone.tagName}
    </span>
  </div>
);

// ─── Main component ─────────────────────────────────────────────────────────

interface Props {
  /** When viewing another user's profile via authenticated endpoint. */
  talentId?: string;
  /** When viewing via share link (unauthenticated). */
  shareToken?: string;
}

const FoundationalKnowledge: FC<Props> = ({talentId, shareToken}) => {
  const {user} = useMaybeNotSetupUser();
  const selfMode = !talentId && !shareToken;
  const email = selfMode && user?.type === "accountant" ? user.email : undefined;

  // ── Self mode: 4 separate CAS endpoints ──────────────────────────────────
  const {data: selfPathways, isLoading: sp} = useQuery({
    queryKey: ["cas-pathways"],
    queryFn: () => accountantApi.getCasPathways(),
    staleTime: 5 * 60_000,
    enabled: selfMode,
  });
  const {data: selfScores, isLoading: ss} = useQuery({
    queryKey: ["bookwidgets-scores"],
    queryFn: () => accountantApi.getBookWidgetsScores(),
    staleTime: 5 * 60_000,
    enabled: selfMode,
  });
  const {data: selfProfile, isLoading: spr} = useQuery({
    queryKey: ["cas-profile"],
    queryFn: () => accountantApi.getCasProfile(),
    staleTime: 5 * 60_000,
    enabled: selfMode,
  });
  const {data: selfCourseGroups, isLoading: scg} = useQuery({
    queryKey: ["cas-course-groups"],
    queryFn: () => accountantApi.getCourseGroups(),
    staleTime: 5 * 60_000,
    enabled: selfMode,
  });

  // ── External mode (talentId): single combined endpoint ───────────────────
  const {data: externalData, isLoading: ep} = useQuery({
    queryKey: ["user-foundational-knowledge", talentId],
    queryFn: () => accountantApi.getUserFoundationalKnowledge(talentId!),
    staleTime: 5 * 60_000,
    enabled: !!talentId,
  });

  // ── Share mode (shareToken): unauthenticated share endpoint ──────────────
  const {data: shareData, isLoading: shp} = useQuery({
    queryKey: ["shared-foundational-knowledge", shareToken],
    queryFn: () => shareApi.getSharedFoundationalKnowledge(shareToken!),
    staleTime: 5 * 60_000,
    enabled: !!shareToken,
  });

  // ── Normalize into common shape ──────────────────────────────────────────
  const combined = shareToken ? shareData : externalData;
  const isLoading = selfMode ? sp || ss || spr || scg : shareToken ? shp : ep;
  const pathways = selfMode ? selfPathways : combined?.pathways;
  const scores = selfMode ? selfScores : combined?.scores;
  const addOnPreferences = useMemo(
    () => (selfMode ? (selfProfile?.addOnPreferences ?? {}) : (combined?.addOnPreferences ?? {})),
    [selfMode, selfProfile?.addOnPreferences, combined?.addOnPreferences],
  );
  const courseGroups = selfMode ? selfCourseGroups : combined?.courseGroups;

  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");

  const addOnGroupDefs = useMemo(() => {
    if (!courseGroups) return [];
    return courseGroups.filter((g) => g.visibilityEditable);
  }, [courseGroups]);

  // Determine which add-on groups the user actually has courses for
  const addOnAccess = useMemo((): Record<string, boolean> => {
    if (!pathways) return {};
    const groupsInPathways = new Set(pathways.flatMap((p) => p.courses.map((c) => c.addOnGroup).filter(Boolean)));
    return Object.fromEntries(addOnGroupDefs.map((g) => [g.groupKey, groupsInPathways.has(g.groupKey)]));
  }, [pathways, addOnGroupDefs]);

  const [addOnOverrides, setAddOnOverrides] = useState<Record<string, boolean>>({});

  const effectiveAddOnPrefs = useMemo((): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    for (const g of addOnGroupDefs) {
      result[g.groupKey] = addOnOverrides[g.groupKey] ?? addOnPreferences[g.groupKey] ?? g.defaultEnabled;
    }
    return result;
  }, [addOnGroupDefs, addOnOverrides, addOnPreferences]);

  const toggleAddOn = (group: string) => {
    setAddOnOverrides((prev) => ({...prev, [group]: !effectiveAddOnPrefs[group]}));
  };

  const allItems = useMemo(() => {
    if (!pathways || !scores || !courseGroups) return [];
    return buildTimeline(pathways, scores, effectiveAddOnPrefs, courseGroups);
  }, [pathways, scores, effectiveAddOnPrefs, courseGroups]);

  const summary = useMemo(() => computeSummary(allItems), [allItems]);

  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (item.type === "milestone") return true;
      const r = item.data;
      if (activeFilter === "completed") return r.status === "complete";
      if (activeFilter === "in_progress") return r.status === "active";
      if (activeFilter === "locked") return r.status === "locked" || r.status === "available";
      return true;
    });
  }, [allItems, activeFilter]);

  const counts = useMemo((): Record<FilterTab, number> => {
    const rows = allItems.filter((i): i is {type: "row"; data: TimelineRow} => i.type === "row").map((i) => i.data);
    return {
      all: rows.length,
      completed: rows.filter((r) => r.status === "complete").length,
      in_progress: rows.filter((r) => r.status === "active").length,
      locked: rows.filter((r) => r.status === "locked" || r.status === "available").length,
    };
  }, [allItems]);

  const filterLabel =
    activeFilter === "all"
      ? "All Courses"
      : activeFilter === "completed"
        ? "Completed"
        : activeFilter === "in_progress"
          ? "In Progress"
          : "Locked";

  if (isLoading) {
    return (
      <div className="space-y-3 py-8">
        {Array.from({length: 5}).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-aka-surface-base animate-pulse border border-aka-border" />
        ))}
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <p className="text-sm text-aka-subtle">No foundational knowledge data available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SummaryStats summary={summary} />
      <FilterButtons activeFilter={activeFilter} setActiveFilter={setActiveFilter} counts={counts} />
      <OverallBar summary={summary} filterLabel={filterLabel} />
      <AddOnToggles
        groups={addOnGroupDefs.map((g) => ({key: g.groupKey, label: g.label}))}
        enabledGroups={effectiveAddOnPrefs}
        access={addOnAccess}
        onToggle={toggleAddOn}
      />

      {/* Course timeline with vertical connector */}
      <div className="relative">
        <div className="absolute bg-aka-border" style={{left: 17, top: 0, bottom: 0, width: 2, zIndex: 0}} />
        <div className="relative space-y-0.5" style={{zIndex: 1}}>
          {filteredItems.map((item) =>
            item.type === "milestone" ? (
              <MilestoneRow key={item.data.tagName} milestone={item.data} />
            ) : (
              <RowItem key={item.data.key} row={item.data} email={email} />
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default FoundationalKnowledge;
